import _classPrivateFieldSet from '@babel/runtime/helpers/classPrivateFieldSet';
import _classPrivateFieldGet from '@babel/runtime/helpers/classPrivateFieldGet';
import { HorizontalScroll, closest, toArray, toggleAttribute, dispatchEvent, addStyle } from '@nrk/dh-basis-common';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _asyncToGenerator from '@babel/runtime/helpers/asyncToGenerator';

function _classPrivateFieldInitSpec$4(obj, privateMap, value) { _checkPrivateRedeclaration$5(obj, privateMap); privateMap.set(obj, value); }
function _checkPrivateRedeclaration$5(obj, privateCollection) { if (privateCollection.has(obj)) { throw new TypeError("Cannot initialize the same private elements twice on an object"); } }
var MOVE_SIGNIFICANT = 10;
var VELOCITY_SIGNIFICANT = 0.3;
function lengthInPx(value) {
  if (value === 'auto') {
    return 0;
  } else if (value.endsWith('px')) {
    return parseInt(value.replace('px'));
  } else if (value.endsWith('%')) {
    throw new Error('scroll percentage in % is not supported');
  }
}

/**
 * Mixin that supports click-dragging and smooth scrolling on a child scroll container.
 * The container must be marked with `[data-scroll-container]`
 *
 * dragCompleteCallback is called each time a drag is complete
 *
 * @template {{prototype: HTMLElement; new(): HTMLElement}} BaseClass
 * @param {BaseClass} Base
 * @mixin SideScrollingMixin
 */
function SideScrollingMixin(Base) {
  var _momentum, _autoScrolling, _handleMouseDown, _handleMouseMove, _handleMouseUp;
  return _momentum = /*#__PURE__*/new WeakMap(), _autoScrolling = /*#__PURE__*/new WeakMap(), _handleMouseDown = /*#__PURE__*/new WeakMap(), _handleMouseMove = /*#__PURE__*/new WeakMap(), _handleMouseUp = /*#__PURE__*/new WeakMap(), class SideScrollingMixinDef extends Base {
    constructor() {
      var _this;
      super(...arguments);
      _this = this;
      /* Values used to simulate momentum on click-drag */
      _classPrivateFieldInitSpec$4(this, _momentum, {
        writable: true,
        value: {
          pageX: 0,
          totalDiffX: 0,
          prevTime: 0,
          velX: 0
        }
      });
      _classPrivateFieldInitSpec$4(this, _autoScrolling, {
        writable: true,
        value: 0
      });
      /**
       * Uses native scrollTo to scroll to a given position. Falls back to a custom scrolling function for
       * browsers that doesn't support smooth scroll behavior (Safari < 15.4)
       *
       * TODO: remove fallback for older browsers once most users have Safari >= 15.5
       *
       * @param left Position to scroll to (scroll-left)
       * @param time duration of the scrolling animation
       * @param callback Callback function once scrolling is complete
       */
      _defineProperty(this, "scrollToPos", function (left) {
        var time = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 600;
        var callback = arguments.length > 2 ? arguments[2] : undefined;
        _classPrivateFieldSet(_this, _autoScrolling, _classPrivateFieldGet(_this, _autoScrolling) + 1);
        var elm = _this.scrollContainer;
        var onDone = () => {
          _classPrivateFieldSet(_this, _autoScrolling, _classPrivateFieldGet(_this, _autoScrolling) - 1);
          callback && callback();
        };
        if (elm && !('scrollBehavior' in elm.style && elm.scrollTo)) {
          // Safari < 15.5 doesn't support scroll behaviour smooth so we have implemented a custom scrollTo.
          if (_this._horizontalScroller) {
            // Cancelling existing scroller. On done will not be called automatically.
            _this._horizontalScroller.cancel();
            _classPrivateFieldSet(_this, _autoScrolling, _classPrivateFieldGet(_this, _autoScrolling) - 1);
            delete _this._horizontalScroller;
          }

          // Safari 15 does not allow manual scrolling with scroll snap active.
          // Disable scroll snap while manually animating scroll.
          elm.style.scrollSnapType = 'none';
          _this._horizontalScroller = new HorizontalScroll(elm);
          requestAnimationFrame(() => _this._horizontalScroller.to(left, time, () => {
            // Scrolling must be allowed to complete before we reset scroll-snap-type.
            // Otherwise both snap and scrollTo tries to animate the scroll at the same time.

            var scrollLeft = elm.scrollLeft;
            elm.style.scrollSnapType = 'x mandatory';
            // Safari <=14 reverts to start after activating scroll snap, so set position manually.
            requestAnimationFrame(() => elm.scrollLeft = scrollLeft);
            delete _this._horizontalScroller;
            onDone();
          }));
        } else if (elm) {
          requestAnimationFrame(() => elm.scrollTo({
            left,
            behavior: time > 0 ? 'smooth' : 'auto'
          }));
          setTimeout(onDone, Math.max(time, 100));
        }
      });
      _classPrivateFieldInitSpec$4(this, _handleMouseDown, {
        writable: true,
        value: event => {
          if (!closest(event.target, '[data-scroll-container]')) {
            return;
          }
          if (closest(event.target, '[data-drag="false"]')) {
            return;
          }
          if (event.button !== 0) {
            return;
          }
          event.preventDefault();
          var scrollContainer = this.scrollContainer;
          _classPrivateFieldGet(this, _momentum).pageX = event.pageX;
          _classPrivateFieldGet(this, _momentum).prevTime = Date.now();
          _classPrivateFieldGet(this, _momentum).totalDiffX = 0;
          scrollContainer.style.cursor = '-webkit-grabbing';
          scrollContainer.style.cursor = 'grabbing';
          scrollContainer.style.scrollSnapType = 'none';

          /* Events are connected to window to handle user dragging outside component */
          window.addEventListener('mousemove', _classPrivateFieldGet(this, _handleMouseMove));
          window.addEventListener('mouseup', _classPrivateFieldGet(this, _handleMouseUp));
        }
      });
      _classPrivateFieldInitSpec$4(this, _handleMouseMove, {
        writable: true,
        value: event => {
          var scrollContainer = this.scrollContainer;
          var diffX = _classPrivateFieldGet(this, _momentum).pageX - event.pageX;
          var time = Date.now();
          var diffTime = time - _classPrivateFieldGet(this, _momentum).prevTime;
          _classPrivateFieldGet(this, _momentum).totalDiffX += diffX;
          _classPrivateFieldGet(this, _momentum).pageX = event.pageX;
          _classPrivateFieldGet(this, _momentum).prevTime = time;
          _classPrivateFieldGet(this, _momentum).velX = diffX / diffTime;
          this._dragging = true;
          scrollContainer.scrollLeft += diffX;

          // Prevent link clicks if scroll container has been click drag/scrolled
          if (Math.abs(_classPrivateFieldGet(this, _momentum).totalDiffX) > MOVE_SIGNIFICANT) {
            scrollContainer.style.pointerEvents = 'none';
          }
        }
      });
      _classPrivateFieldInitSpec$4(this, _handleMouseUp, {
        writable: true,
        value: event => {
          var _this$dragCompleteCal;
          var scrollContainer = this.scrollContainer;
          scrollContainer.style.cursor = 'pointer';
          setTimeout(() => {
            // Safari doesn't support scroll behaviour smooth, so we have implemented a custom scrollTo.
            // This must be allowed to complete before we reset scroll-snap-type.
            // Otherwise both snap and scrollTo tries to animate the scroll at the same time.
            scrollContainer.style.scrollSnapType = 'x mandatory';
          }, 600);
          window.removeEventListener('mousemove', _classPrivateFieldGet(this, _handleMouseMove));
          window.removeEventListener('mouseup', _classPrivateFieldGet(this, _handleMouseUp));
          scrollContainer.style.pointerEvents = '';
          (_this$dragCompleteCal = this.dragCompleteCallback) === null || _this$dragCompleteCal === void 0 || _this$dragCompleteCal.call(this, {
            significant: Math.abs(_classPrivateFieldGet(this, _momentum).totalDiffX) > MOVE_SIGNIFICANT && Math.abs(_classPrivateFieldGet(this, _momentum).velX) > VELOCITY_SIGNIFICANT,
            direction: _classPrivateFieldGet(this, _momentum).velX > 0 ? 'right' : 'left'
          });
        }
      });
    }
    connectedCallback() {
      var _super$connectedCallb;
      (_super$connectedCallb = super.connectedCallback) === null || _super$connectedCallb === void 0 || _super$connectedCallb.call(this);
      this.addEventListener('mousedown', _classPrivateFieldGet(this, _handleMouseDown));
    }
    disconnectedCallback() {
      var _super$disconnectedCa;
      this.removeEventListener('mousedown', _classPrivateFieldGet(this, _handleMouseDown));
      (_super$disconnectedCa = super.disconnectedCallback) === null || _super$disconnectedCa === void 0 || _super$disconnectedCa.call(this);
    }
    get isAutoScrolling() {
      return _classPrivateFieldGet(this, _autoScrolling) > 0;
    }
    get scrollContainer() {
      return this.querySelector('[data-scroll-container]');
    }

    /**
     * Returns a rectangle representing the optimal viewing region of the scroll container:
     * https://www.w3.org/TR/css-scroll-snap-1/#optimal-viewing-region
     *
     * The optimal viewing region is the area of the scroll container minus scroll paddings.
     */
    get optimalViewingRect() {
      var scrollContainer = this.scrollContainer;
      if (!scrollContainer) {
        return undefined;
      }
      var computedStyle = getComputedStyle(scrollContainer);
      var paddingLeft = lengthInPx(computedStyle.scrollPaddingLeft);
      var paddingRight = lengthInPx(computedStyle.scrollPaddingRight);
      var width = scrollContainer.offsetWidth || 0;
      var height = scrollContainer.offsetHeight || 0;
      return {
        top: 0,
        left: paddingLeft,
        bottom: height,
        right: width - paddingRight,
        width: width - paddingLeft - paddingRight,
        height
      };
    }
  };
}

function _classPrivateMethodInitSpec$2(obj, privateSet) { _checkPrivateRedeclaration$4(obj, privateSet); privateSet.add(obj); }
function _classPrivateFieldInitSpec$3(obj, privateMap, value) { _checkPrivateRedeclaration$4(obj, privateMap); privateMap.set(obj, value); }
function _checkPrivateRedeclaration$4(obj, privateCollection) { if (privateCollection.has(obj)) { throw new TypeError("Cannot initialize the same private elements twice on an object"); } }
function _classPrivateMethodGet$2(receiver, privateSet, fn) { if (!privateSet.has(receiver)) { throw new TypeError("attempted to get private field on non-instance"); } return fn; }
var directionInfo = {
  left: {
    increment: -1
  },
  right: {
    increment: 1
  }
};
var NavigationTypes = {
  normal: 'normal',
  skipVisible: 'skip-visible'
};
var clamp$1 = (num, min, max) => Math.min(Math.max(num, min), max);

/**
 * Calculates snap positions of children of a scroll container.
 * The container must be marked with `[data-scroll-container]`.
 *
 * Calculates index of closest snap position, and supports scrolling to next
 * scroll position in a given direction.
 *
 * Scrolls to next scroll position when click drag is complete.
 *
 * @template {new (...args: any[]) => SideScrollingMixinDef} BaseClass
 * @param {BaseClass} Base
 * @mixin
 */
function SnapPositionsMixin(Base) {
  var _snapPositions, _snapIdx, _intervalId, _observer, _getNextScrollPos, _updateSnapIdx;
  return _snapPositions = /*#__PURE__*/new WeakMap(), _snapIdx = /*#__PURE__*/new WeakMap(), _intervalId = /*#__PURE__*/new WeakMap(), _observer = /*#__PURE__*/new WeakMap(), _getNextScrollPos = /*#__PURE__*/new WeakSet(), _updateSnapIdx = /*#__PURE__*/new WeakSet(), class SnapPositionsMixinDef extends Base {
    constructor() {
      super(...arguments);
      _classPrivateMethodInitSpec$2(this, _updateSnapIdx);
      _classPrivateMethodInitSpec$2(this, _getNextScrollPos);
      _classPrivateFieldInitSpec$3(this, _snapPositions, {
        writable: true,
        value: []
      });
      _classPrivateFieldInitSpec$3(this, _snapIdx, {
        writable: true,
        value: 0
      });
      _classPrivateFieldInitSpec$3(this, _intervalId, {
        writable: true,
        value: void 0
      });
      _classPrivateFieldInitSpec$3(this, _observer, {
        writable: true,
        value: void 0
      });
      _defineProperty(this, "updateSnapPositions", () => {
        var _this$items, _this$_positionsUpdat;
        // Set component width custom property. Can be used for sizing items.
        // This can cause reflow when reading computed style and offsetWidth, but there's no way around it atm.
        // Once container queries and units have wide support it can be removed and replaced by 'qw'.
        this.style.setProperty('--dh-basis-component-width', "".concat(this.offsetWidth, "px"));
        var optRect = this.optimalViewingRect;
        if (!optRect) {
          return;
        }
        _classPrivateFieldSet(this, _snapPositions, (_this$items = this.items) === null || _this$items === void 0 ? void 0 : _this$items.map((elm, idx) => {
          var {
            scrollSnapAlign
          } = getComputedStyle(elm);
          var align = (scrollSnapAlign !== null && scrollSnapAlign !== void 0 ? scrollSnapAlign : '').split(' ')[0];
          var targetLeft;
          if (align === 'start') {
            targetLeft = Math.round(optRect.left);
          } else if (align === 'end') {
            targetLeft = Math.round(optRect.right - elm.offsetWidth);
          } else {
            targetLeft = Math.round(optRect.left + optRect.width / 2 - elm.offsetWidth / 2);
          }
          return {
            idx,
            targetLeft,
            targetRight: targetLeft + elm.offsetWidth,
            scrollLeft: elm.offsetLeft - targetLeft
          };
        }));

        // Notify subclasses that snap positions have changed
        (_this$_positionsUpdat = this._positionsUpdatedCallback) === null || _this$_positionsUpdat === void 0 || _this$_positionsUpdat.call(this);
      });
    }
    connectedCallback() {
      var _super$connectedCallb;
      (_super$connectedCallb = super.connectedCallback) === null || _super$connectedCallb === void 0 || _super$connectedCallb.call(this);
      this.updateSnapPositions();
      window.addEventListener('resize', this.updateSnapPositions);
      _classPrivateFieldSet(this, _intervalId, setInterval(() => _classPrivateMethodGet$2(this, _updateSnapIdx, _updateSnapIdx2).call(this), 100));
      _classPrivateFieldSet(this, _observer, new MutationObserver((mutationList, observer) => {
        if (mutationList.some(mutation => mutation.type === 'childList')) {
          this.updateSnapPositions();
        }
      }));
      _classPrivateFieldGet(this, _observer).observe(this, {
        childList: true,
        subtree: true
      });
    }
    disconnectedCallback() {
      var _classPrivateFieldGet2, _super$disconnectedCa;
      window.removeEventListener('resize', this.updateSnapPositions);
      clearInterval(_classPrivateFieldGet(this, _intervalId));
      (_classPrivateFieldGet2 = _classPrivateFieldGet(this, _observer)) === null || _classPrivateFieldGet2 === void 0 || _classPrivateFieldGet2.disconnect();
      (_super$disconnectedCa = super.disconnectedCallback) === null || _super$disconnectedCa === void 0 || _super$disconnectedCa.call(this);
    }

    /* Handle drag complete and snap to next snap position */
    dragCompleteCallback(_ref) {
      var {
        significant,
        direction
      } = _ref;
      /*
       * Scroll to the next card on the left/right if speed is large enough
       * and it has not yet snapped to the next card already:
       * (Simulating momentum)
       * */
      var currentScrollLeft = this.scrollContainer.scrollLeft;
      var activeScrollPos = this.getSnapPosition(_classPrivateFieldGet(this, _snapIdx));
      var leftOfSnapPoint = activeScrollPos.scrollLeft < currentScrollLeft;
      var rightOfSnapPoint = activeScrollPos.scrollLeft > currentScrollLeft;
      var nextScrollPos = activeScrollPos;
      if (significant) {
        if (direction === 'right' && leftOfSnapPoint) {
          nextScrollPos = _classPrivateMethodGet$2(this, _getNextScrollPos, _getNextScrollPos2).call(this, activeScrollPos, direction, NavigationTypes.normal);
        } else if (direction === 'left' && rightOfSnapPoint) {
          nextScrollPos = _classPrivateMethodGet$2(this, _getNextScrollPos, _getNextScrollPos2).call(this, activeScrollPos, direction, NavigationTypes.normal);
        }
      }

      // Always scroll to next to prevent Safari from scrolling to start
      // When resetting scrollSnapType to 'x mandatory' safari scrolls back to start.
      this.scrollToPos(nextScrollPos.scrollLeft);
    }
    /**
     * Does a quick update to scrollLeft assuming the snap target has not changed.
     * This can be run to quickly update scroll positions after padding has been changed.
     */
    _quickUpdateSnapPositions() {
      var items = this.items;
      _classPrivateFieldGet(this, _snapPositions).forEach(pos => {
        var elm = items[pos.idx];
        pos.scrollLeft = elm.offsetLeft - pos.targetLeft;
      });
    }
    getSnapPosition(idx) {
      return _classPrivateFieldGet(this, _snapPositions)[idx];
    }
    findClosestSnapIdx() {
      var scrollContainer = this.scrollContainer;
      if (!scrollContainer) {
        return;
      }
      var {
        scrollLeft
      } = scrollContainer;
      var distances = _classPrivateFieldGet(this, _snapPositions).map(pos => Math.abs(scrollLeft - pos.scrollLeft));
      var min = Math.min(...distances);
      var minIdx = distances.indexOf(min);
      if (minIdx > -1) {
        return minIdx;
      }
    }

    /**
     * Scrolls to next snap point in a given direction.
     *
     * This will hopefully be replaced by native functionality in the future:
     * https://github.com/argyleink/ScrollSnapExplainers/tree/main/js-scrollToOptions_Snap-Additions
     *
     * @param direction {'left' | 'right'} direction to scroll
     * @param navigation {'normal' | 'skip-visible'} navigation strategy
     */
    scrollToNext(direction, navigation) {
      var activeScrollPos = this.getSnapPosition(_classPrivateFieldGet(this, _snapIdx));
      var nextScrollPos = _classPrivateMethodGet$2(this, _getNextScrollPos, _getNextScrollPos2).call(this, activeScrollPos, direction);
      var scrollContainer = this.scrollContainer;
      var maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.offsetWidth;
      this.scrollToPos(clamp$1(nextScrollPos.scrollLeft, 0, maxScrollLeft));
    }

    /* Get the index of the element with the left-most (start) snap position */
    getSnapIndexStart() {
      var sorted = [..._classPrivateFieldGet(this, _snapPositions)].sort((a, b) => a.scrollLeft - b.scrollLeft);
      return sorted[0].idx;
    }

    /* Get the index of the element with the right-most (end) snap position */
    getSnapIndexEnd() {
      var sorted = [..._classPrivateFieldGet(this, _snapPositions)].sort((a, b) => a.scrollLeft - b.scrollLeft);
      return sorted[sorted.length - 1].idx;
    }
    get items() {
      var _this$scrollContainer, _this$scrollContainer2;
      return toArray((_this$scrollContainer = (_this$scrollContainer2 = this.scrollContainer) === null || _this$scrollContainer2 === void 0 ? void 0 : _this$scrollContainer2.children) !== null && _this$scrollContainer !== void 0 ? _this$scrollContainer : []);
    }
    get navigation() {
      var _this$getAttribute;
      return (_this$getAttribute = this.getAttribute('navigation')) !== null && _this$getAttribute !== void 0 ? _this$getAttribute : NavigationTypes.normal;
    }
    set navigation(val) {
      this.setAttribute('navigation', val);
    }
  };
  function _getNextScrollPos2(scrollPos, direction) {
    var _directionInfo$direct, _directionInfo$direct2;
    var navigation = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : this.navigation;
    var sortedPositions = [..._classPrivateFieldGet(this, _snapPositions)].sort((a, b) => a.scrollLeft - b.scrollLeft);
    var idx = sortedPositions.indexOf(scrollPos);
    var increment = (_directionInfo$direct = (_directionInfo$direct2 = directionInfo[direction]) === null || _directionInfo$direct2 === void 0 ? void 0 : _directionInfo$direct2.increment) !== null && _directionInfo$direct !== void 0 ? _directionInfo$direct : 0;
    var nextIdx;
    if (navigation === NavigationTypes.normal) {
      nextIdx = clamp$1(idx + increment, 0, sortedPositions.length - 1);
    } else if (navigation === NavigationTypes.skipVisible) {
      // Greedily search for the next snap position that skips items that were already visible,
      // but ensures that the first item that was (partially) hidden will be visible.
      var scrollContainer = this.scrollContainer;
      var firstHiddenItem; // The first item that was partially or fully hidden
      for (var testIdx = idx; testIdx >= 0 && testIdx <= sortedPositions.length - 1; testIdx += increment) {
        if (!firstHiddenItem) {
          // As long as we have not found a previously hidden element we continue searching.
          nextIdx = testIdx;
          var nextItem = this.items[sortedPositions[nextIdx].idx];
          if (isHidden(scrollContainer.scrollLeft, scrollContainer.offsetWidth, nextItem)) {
            firstHiddenItem = nextItem;
          }
        } else {
          // We have found the first item that was partially or fully hidden.
          // We could stop the search now and snap to this item, but there might be
          // another item we can snap to while still keeping this item visible.
          var nextSnapPosition = sortedPositions[testIdx];

          // When calculating the next scrollLeft position we must take into account that the snap position
          // might not be reachable as it is limited by the scroll container.
          var maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.offsetWidth;
          var nextScrollLeft = clamp$1(nextSnapPosition.scrollLeft, 0, maxScrollLeft);
          if (!isHidden(nextScrollLeft, scrollContainer.offsetWidth, firstHiddenItem)) {
            // The first item that was previously hidden is still visible at this snap point.
            // We can use it as the next snap position.
            nextIdx = testIdx;
          }
        }
      }
    }
    return sortedPositions[nextIdx];
  }
  function _updateSnapIdx2() {
    var nextIdx = this.findClosestSnapIdx();
    if (typeof nextIdx === 'number' && nextIdx !== _classPrivateFieldGet(this, _snapIdx)) {
      var _this$snapIdxChangedC;
      _classPrivateFieldSet(this, _snapIdx, nextIdx);
      (_this$snapIdxChangedC = this.snapIdxChangedCallback) === null || _this$snapIdxChangedC === void 0 || _this$snapIdxChangedC.call(this, nextIdx);
    }
  }
}

/* Check if item is partially hidden */
function isHidden(scrollLeft, offsetWidth, item) {
  return item.offsetLeft < scrollLeft || item.offsetLeft + item.offsetWidth > scrollLeft + offsetWidth;
}

function _classPrivateMethodInitSpec$1(obj, privateSet) { _checkPrivateRedeclaration$3(obj, privateSet); privateSet.add(obj); }
function _classPrivateFieldInitSpec$2(obj, privateMap, value) { _checkPrivateRedeclaration$3(obj, privateMap); privateMap.set(obj, value); }
function _checkPrivateRedeclaration$3(obj, privateCollection) { if (privateCollection.has(obj)) { throw new TypeError("Cannot initialize the same private elements twice on an object"); } }
function _classPrivateMethodGet$1(receiver, privateSet, fn) { if (!privateSet.has(receiver)) { throw new TypeError("attempted to get private field on non-instance"); } return fn; }
var ButtonModes = {
  visibility: 'visibility',
  snapPoints: 'snap-points'
};
var directions = ['left', 'right'];

/**
 * Hides and shows button based on visibility or snap-index of scroll container children
 * Handles clicks and scrolls in the given direction.
 * Emits 'dh-basis-sveip-button-clicked' event with direction
 *
 * Buttons have to be marked with [value="left"] and [value="right"]
 *
 * @template BaseClass {SnapPositionsMixin}
 * @param {BaseClass} Base
 */
function ButtonManagerMixin(Base) {
  var _intersectionObserver, _mutationObserver, _createIntersectionObserver, _disconnectIntersectionObserver, _handleDOMMutation, _handleIntersectionChange, _updateButtonsBasedOnSnapPoints, _handleButtonClick;
  return _intersectionObserver = /*#__PURE__*/new WeakMap(), _mutationObserver = /*#__PURE__*/new WeakMap(), _createIntersectionObserver = /*#__PURE__*/new WeakMap(), _disconnectIntersectionObserver = /*#__PURE__*/new WeakMap(), _handleDOMMutation = /*#__PURE__*/new WeakMap(), _handleIntersectionChange = /*#__PURE__*/new WeakMap(), _updateButtonsBasedOnSnapPoints = /*#__PURE__*/new WeakSet(), _handleButtonClick = /*#__PURE__*/new WeakMap(), class ButtonManagerMixinDef extends Base {
    constructor() {
      super(...arguments);
      _classPrivateMethodInitSpec$1(this, _updateButtonsBasedOnSnapPoints);
      _classPrivateFieldInitSpec$2(this, _intersectionObserver, {
        writable: true,
        value: void 0
      });
      _classPrivateFieldInitSpec$2(this, _mutationObserver, {
        writable: true,
        value: void 0
      });
      _classPrivateFieldInitSpec$2(this, _createIntersectionObserver, {
        writable: true,
        value: () => {
          // eslint-disable-next-line compat/compat
          _classPrivateFieldSet(this, _intersectionObserver, new IntersectionObserver(_classPrivateFieldGet(this, _handleIntersectionChange), {
            threshold: [0.93, 1],
            root: this.scrollContainer
          }));
          for (var item of this.items) {
            _classPrivateFieldGet(this, _intersectionObserver).observe(item);
          }

          // Set up mutation observer to handle changes to children
          _classPrivateFieldSet(this, _mutationObserver, new MutationObserver((mutationList, observer) => {
            if (mutationList.some(mutation => mutation.type === 'childList')) {
              _classPrivateFieldGet(this, _handleDOMMutation).call(this);
            }
          }));
          _classPrivateFieldGet(this, _mutationObserver).observe(this, {
            childList: true,
            subtree: true
          });
        }
      });
      _classPrivateFieldInitSpec$2(this, _disconnectIntersectionObserver, {
        writable: true,
        value: () => {
          if (_classPrivateFieldGet(this, _intersectionObserver)) {
            _classPrivateFieldGet(this, _intersectionObserver).disconnect();
          }
          if (_classPrivateFieldGet(this, _mutationObserver)) {
            _classPrivateFieldGet(this, _mutationObserver).disconnect();
          }
        }
      });
      _classPrivateFieldInitSpec$2(this, _handleDOMMutation, {
        writable: true,
        value: () => {
          if (_classPrivateFieldGet(this, _intersectionObserver)) {
            // If the DOM has been updated, we need to update the list of items we observe

            // Stop observing previous items
            _classPrivateFieldGet(this, _intersectionObserver).disconnect();

            // Start observing current items
            for (var item of this.items) {
              _classPrivateFieldGet(this, _intersectionObserver).observe(item);
            }
          }
        }
      });
      _classPrivateFieldInitSpec$2(this, _handleIntersectionChange, {
        writable: true,
        value: entries => {
          var scrollContainer = this.scrollContainer;
          var scrollLeft = scrollContainer.scrollLeft;
          var scrollRight = scrollContainer.scrollWidth - scrollContainer.offsetWidth - scrollLeft;
          var sortedByOffset = [...this.items].sort((a, b) => a.offsetLeft - b.offsetLeft);
          var leftmostItem = sortedByOffset[0];
          var rightmostItem = sortedByOffset[sortedByOffset.length - 1];
          for (var entry of entries) {
            if (entry.target === leftmostItem) {
              toggleAttribute(this.leftButton, 'hidden', entry.intersectionRatio > 0.93 || scrollLeft <= 0);
            }
            if (entry.target === rightmostItem) {
              toggleAttribute(this.rightButton, 'hidden', entry.intersectionRatio > 0.93 || scrollRight <= 0);
            }
          }
        }
      });
      _classPrivateFieldInitSpec$2(this, _handleButtonClick, {
        writable: true,
        value: e => {
          var btn = closest(e.target, 'button');
          var direction = btn === null || btn === void 0 ? void 0 : btn.getAttribute('value');
          if (!directions.includes(direction)) {
            return;
          }
          e.preventDefault();
          this.scrollToNext(direction);
          dispatchEvent(this, 'dh-basis-sveip-button-clicked', {
            direction
          });
        }
      });
    }
    connectedCallback() {
      var _super$connectedCallb;
      (_super$connectedCallb = super.connectedCallback) === null || _super$connectedCallb === void 0 || _super$connectedCallb.call(this);
      if (this.buttonMode === ButtonModes.visibility) {
        _classPrivateFieldGet(this, _createIntersectionObserver).call(this);
      } else if (this.buttonMode === ButtonModes.snapPoints) {
        _classPrivateMethodGet$1(this, _updateButtonsBasedOnSnapPoints, _updateButtonsBasedOnSnapPoints2).call(this, this.getSnapIndexStart());
      }
      this.addEventListener('click', _classPrivateFieldGet(this, _handleButtonClick));
    }
    disconnectedCallback() {
      var _super$disconnectedCa;
      this.removeEventListener('click', _classPrivateFieldGet(this, _handleButtonClick));
      _classPrivateFieldGet(this, _disconnectIntersectionObserver).call(this);
      (_super$disconnectedCa = super.disconnectedCallback) === null || _super$disconnectedCa === void 0 || _super$disconnectedCa.call(this);
    }
    snapIdxChangedCallback(nextIdx) {
      var _super$snapIdxChanged;
      (_super$snapIdxChanged = super.snapIdxChangedCallback) === null || _super$snapIdxChanged === void 0 || _super$snapIdxChanged.call(this, nextIdx);
      if (this.buttonMode === ButtonModes.snapPoints) {
        _classPrivateMethodGet$1(this, _updateButtonsBasedOnSnapPoints, _updateButtonsBasedOnSnapPoints2).call(this, nextIdx);
      }
    }
    get leftButton() {
      return this.querySelector('button[value="left"]');
    }
    get rightButton() {
      return this.querySelector('button[value="right"]');
    }
    get buttonMode() {
      var _this$getAttribute;
      return (_this$getAttribute = this.getAttribute('button-mode')) !== null && _this$getAttribute !== void 0 ? _this$getAttribute : ButtonModes.visibility;
    }
    set buttonMode(val) {
      this.setAttribute('button-mode', val);
    }
  };
  function _updateButtonsBasedOnSnapPoints2(index) {
    toggleAttribute(this.leftButton, 'hidden', index === this.getSnapIndexStart());
    toggleAttribute(this.rightButton, 'hidden', index === this.getSnapIndexEnd());
  }
}

function _classPrivateMethodInitSpec(obj, privateSet) { _checkPrivateRedeclaration$2(obj, privateSet); privateSet.add(obj); }
function _checkPrivateRedeclaration$2(obj, privateCollection) { if (privateCollection.has(obj)) { throw new TypeError("Cannot initialize the same private elements twice on an object"); } }
function _classPrivateMethodGet(receiver, privateSet, fn) { if (!privateSet.has(receiver)) { throw new TypeError("attempted to get private field on non-instance"); } return fn; }
function wait(time) {
  return new Promise(resolve => {
    setTimeout(resolve, time);
  });
}

/**
 *
 * @template BaseClass {SnapPositionsMixin}
 * @param {BaseClass} Base
 */
function StartIndexMixin(Base) {
  var _autoscrollToStartIndex, _calculateStartIndex, _calculateAutoStartIndex;
  return _autoscrollToStartIndex = /*#__PURE__*/new WeakSet(), _calculateStartIndex = /*#__PURE__*/new WeakSet(), _calculateAutoStartIndex = /*#__PURE__*/new WeakSet(), class StartIndexMixinDef extends Base {
    constructor() {
      super(...arguments);
      _classPrivateMethodInitSpec(this, _calculateAutoStartIndex);
      _classPrivateMethodInitSpec(this, _calculateStartIndex);
      _classPrivateMethodInitSpec(this, _autoscrollToStartIndex);
    }
    connectedCallback() {
      var _super$connectedCallb;
      (_super$connectedCallb = super.connectedCallback) === null || _super$connectedCallb === void 0 || _super$connectedCallb.call(this);
      return _classPrivateMethodGet(this, _autoscrollToStartIndex, _autoscrollToStartIndex2).call(this);
    }
    disconnectedCallback() {
      var _super$disconnectedCa;
      (_super$disconnectedCa = super.disconnectedCallback) === null || _super$disconnectedCa === void 0 || _super$disconnectedCa.call(this);
    }
    set startIndex(val) {
      this.setAttribute('start-index', val);
    }
    get startIndex() {
      var _this$getAttribute;
      return (_this$getAttribute = this.getAttribute('start-index')) !== null && _this$getAttribute !== void 0 ? _this$getAttribute : 'auto';
    }
  };
  function _autoscrollToStartIndex2() {
    return _autoscrollToStartIndex3.apply(this, arguments);
  }
  function _autoscrollToStartIndex3() {
    _autoscrollToStartIndex3 = _asyncToGenerator(function* () {
      yield wait(50);
      var startIndex = _classPrivateMethodGet(this, _calculateStartIndex, _calculateStartIndex2).call(this);
      if (typeof startIndex === 'number') {
        this.scrollToPos(this.getSnapPosition(startIndex).scrollLeft, 0);

        /* Retry after 200ms. Safari 16 sometimes igonres elm.scrollTo */
        yield wait(200);
        this.scrollToPos(this.getSnapPosition(startIndex).scrollLeft, 0);
      }
    });
    return _autoscrollToStartIndex3.apply(this, arguments);
  }
  function _calculateStartIndex2() {
    if (this.startIndex === 'none') {
      return null;
    } else if (this.startIndex === 'auto') {
      return _classPrivateMethodGet(this, _calculateAutoStartIndex, _calculateAutoStartIndex2).call(this);
    } else {
      return parseInt(this.startIndex);
    }
  }
  function _calculateAutoStartIndex2() {
    var optimalViewingRect = this.optimalViewingRect;
    if (!optimalViewingRect) {
      return;
    }

    // Calculate width of left half of container, including scroll padding
    var containerLeftHalf = optimalViewingRect.width / 2 + optimalViewingRect.left;
    var items = this.items;
    if (items.length >= 3 && containerLeftHalf > items[0].offsetWidth + items[1].offsetWidth / 2) {
      // Container is wide enough to show first item, when second item is selected
      return 1;
    } else {
      return 0;
    }
  }
}

function _classPrivateFieldInitSpec$1(obj, privateMap, value) { _checkPrivateRedeclaration$1(obj, privateMap); privateMap.set(obj, value); }
function _checkPrivateRedeclaration$1(obj, privateCollection) { if (privateCollection.has(obj)) { throw new TypeError("Cannot initialize the same private elements twice on an object"); } }

/**
 *
 * Calculates and sets padding on the scroll container, to ensure every snap position can be reached.
 *
 * (We have added an issue to get a standardised solution:
 * https://github.com/w3c/csswg-drafts/issues/7885#issuecomment-1368194918.
 * Suggested name for property is `scroll-contain: snap`)
 *
 * I.e. if items have snap-align: center, then this mixin will add enough padding so that the first and the
 * last item can be snapped to the center of the scroll container.
 *
 * As not all browsers include the end-side padding in the scrollable area, we use ::before and ::after pseudo elements.
 * As they generate extra gaps, special care must be taken when calculating the width of the pseudo elements.
 * Negative margins are used to counter the extra gaps.
 * Once all browsers are compatible with the spec we can switch to regular padding.
 *
 * https://bugs.webkit.org/show_bug.cgi?id=236634
 *
 * @template BaseClass {SnapPositionsMixin}
 * @param {BaseClass} Base
 */
function SidePaddingMixin(Base) {
  var _updatePaddingProperties;
  return _updatePaddingProperties = /*#__PURE__*/new WeakMap(), class SidePaddingMixinDef extends Base {
    constructor() {
      super(...arguments);
      _classPrivateFieldInitSpec$1(this, _updatePaddingProperties, {
        writable: true,
        value: () => {
          if (!this.scrollContainer) {
            return;
          }
          var items = this.items;
          var containerWidth = this.scrollContainer.offsetWidth;
          var gap = getComputedStyle(this.scrollContainer).columnGap;
          var gapWidth = 0;
          if (gap.match(/^(\d)+(\.\d+)?px$/)) {
            gapWidth = Math.round(parseFloat(gap));
          }
          var paddingLeft = this.getSnapPosition(0).targetLeft;
          var paddingRight = containerWidth - this.getSnapPosition(items.length - 1).targetRight;
          this.style.setProperty('--_dh-basis-sveip-left-padding', "".concat(paddingLeft, "px"));
          this.style.setProperty('--_dh-basis-sveip-right-padding', "".concat(paddingRight, "px"));
          this.style.setProperty('--_dh-basis-sveip-gap-fix', "".concat(gapWidth, "px"));
        }
      });
    }
    connectedCallback() {
      var _super$connectedCallb;
      (_super$connectedCallb = super.connectedCallback) === null || _super$connectedCallb === void 0 || _super$connectedCallb.call(this);
      /*
      Only add left and right padding if `scroll-contain` equals `snap`.
      No extra padding should be added if `scroll-contain` is `normal`.
      */
      if (this.scrollContain !== 'snap') {
        return;
      }
      addStyle("".concat(this.nodeName, "-padding"), "\n       \n      ".concat(this.nodeName, ":not([scroll-contain=\"normal\"]) [data-scroll-container]::before {\n        content: '';\n        display: block;\n        flex: 0 0 auto;\n        height: 1px;\n        width: var(--_dh-basis-sveip-left-padding);\n        margin-right: calc(-1 * var(--_dh-basis-sveip-gap-fix, 0px));\n      }\n      \n      ").concat(this.nodeName, ":not([scroll-contain=\"normal\"]) [data-scroll-container]::after {\n        content: '';\n        display: block;\n        flex: 0 0 auto;\n        height: 1px;\n        width: var(--_dh-basis-sveip-right-padding);\n        margin-left: calc(-1 * var(--_dh-basis-sveip-gap-fix, 0px));\n      }\n    "));
      _classPrivateFieldGet(this, _updatePaddingProperties).call(this);

      // Update snap positions again to account for the new padding
      this._quickUpdateSnapPositions();
    }
    disconnectedCallback() {
      var _super$disconnectedCa;
      (_super$disconnectedCa = super.disconnectedCallback) === null || _super$disconnectedCa === void 0 || _super$disconnectedCa.call(this);
    }
    _positionsUpdatedCallback() {
      _classPrivateFieldGet(this, _updatePaddingProperties).call(this);
      // Update snap positions again to account for the new padding
      this._quickUpdateSnapPositions();
    }
    /*
    Get `scroll-contain` attribute.
    Scroll-contain determines if the scrollable overflow area contains snap-positions or not.
     Supported modes are `normal` and `snap`.
    Defaults to `snap` if not set.
    */
    get scrollContain() {
      var _this$getAttribute;
      return (_this$getAttribute = this.getAttribute('scroll-contain')) !== null && _this$getAttribute !== void 0 ? _this$getAttribute : 'snap';
    }
    set scrollContain(val) {
      this.setAttribute('scroll-contain', val);
    }
  };
}

function _classPrivateFieldInitSpec(obj, privateMap, value) { _checkPrivateRedeclaration(obj, privateMap); privateMap.set(obj, value); }
function _checkPrivateRedeclaration(obj, privateCollection) { if (privateCollection.has(obj)) { throw new TypeError("Cannot initialize the same private elements twice on an object"); } }
var clamp = (num, min, max) => Math.min(Math.max(num, min), max);

/**
 * Sveip is a component that lets the user swipe horizontally between elements in a scroll container.
 * Content is scrollable with mouse interaction on non-touch-devices.
 *
 * The component is based on standard CSS scroll snapping: https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Scroll_Snap
 * It snaps to scroll positions indicated by the `scroll-snap-align` css property of its child elements.
 * As a default every child has `scroll-snap-align: center`, but this can be overridden with css.
 *
 * The component tracks which element is snapped and marks it with a 'data-state=ACTIVE' attribute.
 * It also emits a 'dh-basis-sveip-active-changed' event when the active element has changed.
 *
 * The functionality is split into separate mixins:
 *
 * – SideScrollingMixin: Is responsible for the scroll container. Handles click-drag and scrolling.
 * – SnapPositionMixin: Calculates snap positions.
 * – ButtonManagerMixin: Manages button interaction and visibility.
 * – SidePaddingMixin: Manages scroll container padding to ensure each snap point is reachable.
 * – StartIndexMixin: Determines which item should be active at start.
 */
var _activeIndex = /*#__PURE__*/new WeakMap();
var _initializing = /*#__PURE__*/new WeakMap();
var _updateAttributes = /*#__PURE__*/new WeakMap();
class Sveip extends StartIndexMixin(SidePaddingMixin(ButtonManagerMixin(SnapPositionsMixin(SideScrollingMixin(HTMLElement))))) {
  constructor() {
    super(...arguments);
    _classPrivateFieldInitSpec(this, _activeIndex, {
      writable: true,
      value: 0
    });
    _classPrivateFieldInitSpec(this, _initializing, {
      writable: true,
      value: 1
    });
    _classPrivateFieldInitSpec(this, _updateAttributes, {
      writable: true,
      value: () => {
        var indicators = this.indicators;
        if (indicators) {
          toArray(this.indicators.children).forEach((indicator, idx) => toggleAttribute(indicator, 'data-indicator-active', idx === _classPrivateFieldGet(this, _activeIndex)));
        }
        this.items.forEach((child, idx) => {
          if (idx === _classPrivateFieldGet(this, _activeIndex)) {
            child.setAttribute('data-state', 'ACTIVE');
          } else {
            child.removeAttribute('data-state');
          }
        });
      }
    });
  }
  connectedCallback() {
    var _super$connectedCallb;
    (_super$connectedCallb = super.connectedCallback) === null || _super$connectedCallb === void 0 || _super$connectedCallb.call(this);
    addStyle(this.nodeName, "\n      ".concat(this.nodeName, " {\n          display: block;\n          position: relative;\n      }\n      \n      ").concat(this.nodeName, " [data-scroll-container] {\n          -webkit-overflow-scrolling: touch;\n          -ms-overflow-style: -ms-autohiding-scrollbar;\n          scrollbar-width: none;\n          cursor: pointer;\n          display: flex;\n          overflow-x: scroll;\n          scroll-snap-type: x mandatory;\n       }\n      \n      ").concat(this.nodeName, " [data-scroll-container] > * {\n          flex: 0 0 auto;\n          scroll-snap-align: center;\n       }\n      \n      ").concat(this.nodeName, " [data-scroll-container]::-webkit-scrollbar {\n        -webkit-appearance: none;\n        display: none;\n        width: 0;\n        height: 0;\n      }\n      \n      ").concat(this.nodeName, " [data-drag=\"false\"] {\n        cursor: auto;\n      }\n    "));
    setTimeout(() => {
      _classPrivateFieldGet(this, _updateAttributes).call(this);

      // Update snap positions in case subclass has programmatically updated sizes
      this.updateSnapPositions();
    });

    // Give the component 500ms to complete initialization before removing the flag.
    // This will allow for the initial layout and scrolling to complete without
    // triggering events
    setTimeout(() => {
      _classPrivateFieldSet(this, _initializing, _classPrivateFieldGet(this, _initializing) - 1);
    }, 500);
  }
  disconnectedCallback() {
    var _super$disconnectedCa;
    (_super$disconnectedCa = super.disconnectedCallback) === null || _super$disconnectedCa === void 0 || _super$disconnectedCa.call(this);
  }
  snapIdxChangedCallback(nextIdx) {
    var _super$snapIdxChanged;
    (_super$snapIdxChanged = super.snapIdxChangedCallback) === null || _super$snapIdxChanged === void 0 || _super$snapIdxChanged.call(this, nextIdx);
    if (typeof nextIdx !== 'number') {
      return;
    }
    _classPrivateFieldSet(this, _activeIndex, nextIdx);
    _classPrivateFieldGet(this, _updateAttributes).call(this);
    if (!_classPrivateFieldGet(this, _initializing)) {
      dispatchEvent(this, 'dh-basis-sveip-active-changed', {
        nextIndex: nextIdx
      });
    }
  }
  scrollToIndex(idx) {
    var clampedIndex = clamp(idx, 0, this.items.length - 1);
    this.scrollToPos(this.getSnapPosition(clampedIndex).scrollLeft);
  }
  get indicators() {
    return this.querySelector('[data-scroll-indicator]');
  }
}

export { Sveip as default };
//# sourceMappingURL=index.esm.js.map
